/**
 * cookieUtils.js — Centralized cookie management for SSO and auth flows.
 *
 * Fixes:
 *  - Explicit { path: '/' } on every set/remove to prevent stale cookie collisions
 *  - Removes cookies on parent domain as well (handles cross-subdomain leftovers)
 *  - Ensures session timer cookies (start/expiry) are always set alongside identity cookies
 */
import CryptoJS from "crypto-js";
import Cookies from "js-cookie";

// ---------- cookie name constants ----------

const AGENT_COOKIES = [
  "comp_nme__",
  "name__",
  "title__",
  "phone__",
  "adress__",
  "city__",
  "state__",
  "zip__",
  "country__",
  "eml__",
  "acn__",
  "tkn__",
  "agent_type__",
  "agent_parent_accountno__",
  "agent_main_accountno__",
  "agent_session_start__",
  "agent_session_expiry__",
];

const CLIENT_COOKIES = [
  "client_name__",
  "client_comp_nme__",
  "client_eml__",
  "client_acn__",
  "client_tkn__",
  "client_phone__",
  "client_title__",
  "client_parent_accountno__",
  "client_user_type__",
  "client_adress__",
  "client_country__",
  "client_return_url__",
  "client_domain_name__",
  "client_session_start__",
  "client_session_expiry__",
  "filer_id__",
];

const RESELLER_COOKIES = [
  "residentifier__",
  "restitle__",
  "reslogo__",
  "ressig__",
  "restkn__",
  "resacct__",
];

const ADMIN_COOKIES = [
  "admin_name__",
  "admin_type__",
  "admin_eml__",
  "admin_acc__",
  "admin_token__",
];

// ---------- helpers ----------

const COOKIE_OPTS = { path: "/" };

function getParentDomain() {
  try {
    const parts = window.location.hostname.split(".");
    if (parts.length >= 2) {
      return "." + parts.slice(-2).join(".");
    }
  } catch (_e) {
    // ignore
  }
  return null;
}

/**
 * Remove a single cookie on both the current path and the parent domain
 * to handle stale cookies left by redirects across subdomains.
 */
function removeCookie(name) {
  Cookies.remove(name, COOKIE_OPTS);
  const parentDomain = getParentDomain();
  if (parentDomain) {
    Cookies.remove(name, { path: "/", domain: parentDomain });
  }
}

function encrypt(value) {
  return CryptoJS.AES.encrypt(
    String(value ?? ""),
    process.env.REACT_APP_SIGNATURE
  ).toString();
}

// ---------- session timer helpers ----------

/** Default session duration — 4 hours (matches backend JWT expiresIn). */
const SESSION_DURATION_MS = 4 * 60 * 60 * 1000;

function setSessionTimerCookies(tokenExpiryMs) {
  const currentTime = new Date().getTime();
  const expiryTime = tokenExpiryMs || currentTime + SESSION_DURATION_MS;

  Cookies.set("client_session_start__", encrypt(currentTime.toString()), COOKIE_OPTS);
  Cookies.set("client_session_expiry__", encrypt(expiryTime.toString()), COOKIE_OPTS);
}

function setAgentSessionTimerCookies(tokenExpiryMs) {
  const currentTime = new Date().getTime();
  const expiryTime = tokenExpiryMs || currentTime + SESSION_DURATION_MS;

  Cookies.set("agent_session_start__", encrypt(currentTime.toString()), COOKIE_OPTS);
  Cookies.set("agent_session_expiry__", encrypt(expiryTime.toString()), COOKIE_OPTS);
}

// ---------- clear helpers ----------

function clearClientSession() {
  CLIENT_COOKIES.forEach(removeCookie);
  RESELLER_COOKIES.forEach(removeCookie);

  // Cross-subdomain session indicator
  removeCookie("client_sl_session__");
  const parentDomain = getParentDomain();
  if (parentDomain) {
    Cookies.remove("client_sl_session__", { path: "/", domain: parentDomain });
  }

  localStorage.removeItem("activeMenusClient");
  localStorage.removeItem("activeSubmenuItemClient");
  localStorage.removeItem("client_sso_handshake");

  // 2026-10-14: one-shot marker so the login page's own "already logged in,
  // skip the form" redirect doesn't fire on the very next load - every
  // logout path (this function) sets it; Login/index.js's constructor reads
  // and consumes it once. Session-scoped, not persisted across tabs/devices
  // on purpose - this is only defense-in-depth (clearing the cookies above
  // already means isClientLogin() is false by the time any page reloads),
  // not the thing actually preventing the stale-session relaunch loop.
  try {
    sessionStorage.setItem("justLoggedOut", "1");
  } catch (e) {
    // Private browsing / storage disabled - the cookie-clearing above still
    // does the real work either way.
  }
}

function clearAgentSession() {
  AGENT_COOKIES.forEach(removeCookie);
  RESELLER_COOKIES.forEach(removeCookie);

  localStorage.removeItem("activeMenusCp");
  localStorage.removeItem("activeSubmenuItemCp");
}

// ---------- exports ----------

export {
  AGENT_COOKIES,
  CLIENT_COOKIES,
  RESELLER_COOKIES,
  ADMIN_COOKIES,
  COOKIE_OPTS,
  removeCookie,
  encrypt,
  getParentDomain,
  setSessionTimerCookies,
  setAgentSessionTimerCookies,
  clearClientSession,
  clearAgentSession,
  SESSION_DURATION_MS,
};
